import { useEffect, useMemo, useRef, useState } from 'react';

import { PageHeader } from '@/components/app-shell/PageHeader';
import { useAuthStore } from '@/features/auth/auth-store';
import { CustomerDrawer } from '@/features/campaigns/drawer/CustomerDrawer';
import { useToast } from '@/features/campaigns/toast-context';
import { useCampaignCustomers } from '@/features/campaigns/useCampaignCustomers';
import type { WizardInit } from '@/features/campaigns/wizard/campaign-state';
import { useProducts } from '@/features/product-scan/useProducts';

import { DenyModal } from './DenyModal';
import { ReassignModal } from './ReassignModal';
import {
  REFERRAL_STATUS_META,
  type Referral,
  type ReferralStatus,
  isMine,
  useReferralsStore,
} from './referrals-store';
import { type ReferralMenuAction } from './referral-menu';
import { exportReferralsCsv } from './referral-export';
import { ReferralsTable } from './ReferralsTable';
import { useReferralsSync } from './useReferralsSync';
import { ReferralSummaryTiles } from './ReferralSummaryTiles';
import { TellerNudgeBanner } from './TellerNudgeBanner';

export interface ReferralsPageProps {
  /** Pre-scope from the Dashboard "View all →" (consume-once). */
  initialAssignedToMe?: boolean;
  onScopeConsumed?: () => void;
  /** Open the Campaigns page with the wizard pre-staged (approve flows). */
  onOpenWizard: (init: WizardInit) => void;
}

/**
 * The Referrals console (SCRUM-329) — v6 `renderReferralsPage`: status
 * summary tiles (full-set counts, click-to-filter), the 30-day expiry note,
 * scope tabs (All referrals / Assigned to me), the FILTERED BY strip with
 * "N of M", the referrals table with per-status action menus, Reassign /
 * Deny modals (single + batch), checkbox selection with the floating batch
 * bar, and the customer inspector drawer beside the table (436px reserved,
 * v6 with-inspector layout).
 */
export function ReferralsPage({
  initialAssignedToMe = false,
  onScopeConsumed,
  onOpenWizard,
}: ReferralsPageProps) {
  const { data: customers, isLoading: customersLoading } = useCampaignCustomers();
  const { data: products, isLoading: productsLoading } = useProducts();
  const referrals = useReferralsStore((s) => s.referrals);
  const seedFromLiveData = useReferralsStore((s) => s.seedFromLiveData);
  const approve = useReferralsStore((s) => s.approve);
  const markReviewed = useReferralsStore((s) => s.markReviewed);
  const reopen = useReferralsStore((s) => s.reopen);
  const clearUnread = useReferralsStore((s) => s.clearUnread);
  const { showToast } = useToast();
  const user = useAuthStore((s) => s.user);

  const [statusFilter, setStatusFilter] = useState<ReferralStatus | null>(null);
  const [assignedToMe, setAssignedToMe] = useState(initialAssignedToMe);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [inspectingCustomerId, setInspectingCustomerId] = useState<string | null>(null);
  const [modal, setModal] = useState<{ kind: 'reassign' | 'deny'; referrals: Referral[] } | null>(
    null,
  );

  // Consume the Dashboard pre-scope once (same contract as the wizard deep-link).
  const scopeConsumedRef = useRef(false);
  useEffect(() => {
    if (!scopeConsumedRef.current) {
      scopeConsumedRef.current = true;
      onScopeConsumed?.();
    }
  }, [onScopeConsumed]);

  // Seed once both queries settle (success OR error — SCRUM-324 lesson).
  const safeCustomers = useMemo(() => customers ?? [], [customers]);
  const safeProducts = useMemo(() => products ?? [], [products]);
  useEffect(() => {
    if (!customersLoading && !productsLoading) seedFromLiveData(safeCustomers, safeProducts);
  }, [customersLoading, productsLoading, safeCustomers, safeProducts, seedFromLiveData]);

  // SCRUM-343: merge server referrals on top of the seeds once data settles.
  useReferralsSync(safeCustomers, !customersLoading && !productsLoading);

  const mineTotal = referrals.filter((r) => isMine(r, user?.email)).length;

  const visible = useMemo(() => {
    let rows = [...referrals];
    if (assignedToMe) rows = rows.filter((r) => isMine(r, user?.email));
    if (statusFilter) rows = rows.filter((r) => r.status === statusFilter);
    return rows.sort((a, b) => a.submittedDaysAgo - b.submittedDaysAgo);
  }, [referrals, assignedToMe, statusFilter, user?.email]);

  const inspectingCustomer = inspectingCustomerId
    ? safeCustomers.find((c) => c.id === inspectingCustomerId)
    : undefined;

  const notify = (title: string, body: string) => {
    showToast({ title, body, variant: 'success' });
  };

  const openCustomer = (r: Referral) => {
    setMenuOpenId(null);
    if (r.tellerUnread && r.tellerName === user?.displayName) clearUnread(r.id);
    if (r.customerId && safeCustomers.some((c) => c.id === r.customerId)) {
      setInspectingCustomerId(r.customerId);
    }
  };

  const startCampaignFor = (rows: Referral[], name: string) => {
    for (const r of rows) approve(r.id);
    const ids = rows.map((r) => r.customerId).filter(Boolean);
    const first = rows[0];
    onOpenWizard({
      targetIds: ids,
      productId: rows.length === 1 && first ? first.productId || null : null,
      productPreSelected: rows.length === 1 && Boolean(first?.productId),
      campaignName: name,
      ...(rows.length === 1 && first ? { linkedReferralId: first.id } : {}),
    });
  };

  const onMenuAction = (r: Referral, action: ReferralMenuAction) => {
    switch (action) {
      case 'approve':
        startCampaignFor([r], `${r.productName} — ${r.customerName} referral`);
        break;
      case 'markReviewed':
        markReviewed(r.id);
        break;
      case 'seeProfile':
        openCustomer(r);
        break;
      case 'reassign':
        setModal({ kind: 'reassign', referrals: [r] });
        break;
      case 'deny':
        setModal({ kind: 'deny', referrals: [r] });
        break;
      case 'reopen':
        reopen(r.id);
        break;
    }
  };

  const selectedReferrals = referrals.filter((r) => selectedIds.has(r.id));
  const actionableSelection = selectedReferrals.filter(
    (r) => r.status === 'pending' || r.status === 'reviewed',
  );

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Referrals"
        subtitle="Product referral submissions and tracking"
        actions={
          <button
            type="button"
            onClick={() => {
              exportReferralsCsv(visible);
            }}
            disabled={visible.length === 0}
            className="border-border-1 bg-bg-2 text-text-1 rounded-tk-md hover:bg-bg-3 mt-1 inline-flex items-center gap-2 border px-3.5 py-2 text-[0.8125rem] font-medium disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg
              viewBox="0 0 16 16"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M8 2v8M4.5 6.5 8 10l3.5-3.5M3 13h10" />
            </svg>
            Export
          </button>
        }
      />

      <div className="flex w-full flex-col gap-4 p-8">
        <TellerNudgeBanner
          referrals={referrals}
          userRole={user?.role}
          displayName={user?.displayName}
        />

        <ReferralSummaryTiles
          referrals={referrals}
          activeFilter={statusFilter}
          onToggle={(s) => {
            setStatusFilter((cur) => (cur === s ? null : s));
          }}
        />

        <p className="text-text-3 -mt-1 inline-flex items-center gap-1.5 text-[0.6875rem] italic">
          <svg
            viewBox="0 0 12 12"
            width="11"
            height="11"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            aria-hidden="true"
            className="flex-shrink-0"
          >
            <circle cx="6" cy="6" r="5" />
            <path d="M6 5.5V9M6 3.2v.1" />
          </svg>
          Referrals expire 30 days after submission.
        </p>

        {/* Scope tabs */}
        <div className="bg-bg-2 border-border-0 rounded-tk-md inline-flex w-fit gap-0.5 border p-[3px]">
          {(
            [
              { key: false, label: 'All referrals', count: null },
              { key: true, label: 'Assigned to me', count: mineTotal },
            ] as const
          ).map((tab) => (
            <button
              key={tab.label}
              type="button"
              aria-pressed={assignedToMe === tab.key}
              onClick={() => {
                setAssignedToMe(tab.key);
              }}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-[5px] text-[0.8125rem] font-medium transition-colors ${
                assignedToMe === tab.key
                  ? 'bg-bg-4 text-text-0 shadow-sm'
                  : 'text-text-2 hover:text-text-0'
              }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span
                  className={`rounded-tk-full min-w-[18px] px-1.5 py-px text-center font-mono text-[0.625rem] font-bold ${
                    assignedToMe === tab.key
                      ? 'text-brand-primary bg-brand-dim'
                      : 'text-text-3 bg-bg-4 border-border-1 border'
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* FILTERED BY strip */}
        {statusFilter && (
          <div className="-mt-1 flex items-center gap-2.5 px-0.5">
            <span className="text-text-3 font-mono text-[0.625rem] font-semibold tracking-[0.08em] uppercase">
              Filtered by
            </span>
            <span className="bg-brand-dim rounded-tk-full text-brand-primary inline-flex items-center gap-1.5 border border-[var(--border-accent)] py-[3px] pr-1 pl-2.5 text-xs font-medium">
              {REFERRAL_STATUS_META[statusFilter].label}
              <button
                type="button"
                aria-label="Clear status filter"
                onClick={() => {
                  setStatusFilter(null);
                }}
                className="text-brand-primary hover:bg-brand-primary rounded-tk-full inline-flex h-4 w-4 items-center justify-center hover:text-white"
              >
                <svg
                  viewBox="0 0 12 12"
                  width="8"
                  height="8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M2 2l8 8M10 2l-8 8" />
                </svg>
              </button>
            </span>
            <span className="text-text-3 ml-auto font-mono text-xs">
              {visible.length} of {referrals.length}
            </span>
          </div>
        )}

        {/* Ledger area: table + optional inspector drawer (v6 with-inspector). */}
        <div
          className="relative w-full transition-[padding-right] duration-200"
          style={inspectingCustomer ? { paddingRight: 436 } : undefined}
        >
          {visible.length === 0 ? (
            <div className="bg-bg-3 border-border-1 rounded-tk-md border border-dashed px-5 py-[60px] text-center">
              <p className="text-text-1 text-[0.9375rem]">
                {statusFilter
                  ? `No referrals with status “${REFERRAL_STATUS_META[statusFilter].label}”.`
                  : 'No referrals yet.'}
              </p>
              {statusFilter ? (
                <button
                  type="button"
                  onClick={() => {
                    setStatusFilter(null);
                  }}
                  className="text-brand-primary hover:text-text-0 mt-2 text-[0.8125rem] underline"
                >
                  Clear filter to see all referrals.
                </button>
              ) : (
                <p className="text-text-3 mt-2 text-[0.8125rem]">
                  Send a referral from a customer’s profile and it will appear here.
                </p>
              )}
            </div>
          ) : (
            <ReferralsTable
              referrals={visible}
              inspectingCustomerId={inspectingCustomerId}
              selectedIds={selectedIds}
              menuOpenId={menuOpenId}
              onToggleSelect={(id) => {
                setSelectedIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                });
              }}
              onToggleSelectAll={() => {
                setSelectedIds((prev) => {
                  const allVisible = visible.every((r) => prev.has(r.id));
                  if (allVisible) return new Set();
                  return new Set(visible.map((r) => r.id));
                });
              }}
              onToggleMenu={setMenuOpenId}
              onRowOpen={openCustomer}
              onMenuAction={onMenuAction}
            />
          )}

          {inspectingCustomer && (
            <div className="absolute top-0 right-0 bottom-0 w-[420px]">
              <CustomerDrawer
                customer={inspectingCustomer}
                products={safeProducts}
                onClose={() => {
                  setInspectingCustomerId(null);
                }}
                onStartCampaign={(customerId) => {
                  onOpenWizard({ targetIds: [customerId] });
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Floating batch action bar */}
      {selectedIds.size > 0 && (
        <div className="rounded-tk-lg shadow-tk-lg bg-bg-4 fixed bottom-6 left-1/2 z-[99] flex -translate-x-1/2 items-center gap-4 border border-[var(--border-accent)] px-4 py-3">
          <span className="bg-brand-dim text-brand-primary rounded-tk-full px-2.5 py-1 font-mono text-xs font-semibold tracking-[0.02em]">
            {selectedIds.size} selected
          </span>
          <button
            type="button"
            onClick={() => {
              setSelectedIds(new Set());
            }}
            className="text-text-3 hover:text-text-0 text-xs"
          >
            Clear
          </button>
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (actionableSelection.length > 0)
                  setModal({ kind: 'deny', referrals: actionableSelection });
              }}
              className="bg-bg-3 border-border-1 rounded-tk-md text-text-1 hover:bg-bg-2 hover:text-text-0 border px-3.5 py-[7px] text-[0.8125rem] font-medium"
            >
              Deny
            </button>
            <button
              type="button"
              onClick={() => {
                if (actionableSelection.length > 0)
                  setModal({ kind: 'reassign', referrals: actionableSelection });
              }}
              className="bg-bg-3 border-border-1 rounded-tk-md text-text-1 hover:bg-bg-2 hover:text-text-0 border px-3.5 py-[7px] text-[0.8125rem] font-medium"
            >
              Reassign
            </button>
            <button
              type="button"
              onClick={() => {
                if (actionableSelection.length === 0) return;
                startCampaignFor(
                  actionableSelection,
                  `Referral batch — ${String(actionableSelection.length)} referrals`,
                );
                setSelectedIds(new Set());
              }}
              className="bg-brand-primary rounded-tk-md px-3.5 py-[7px] text-[0.8125rem] font-semibold text-white"
            >
              Approve
            </button>
          </span>
        </div>
      )}

      {modal?.kind === 'reassign' && (
        <ReassignModal
          referrals={modal.referrals}
          notify={notify}
          onClose={() => {
            setModal(null);
            setSelectedIds(new Set());
          }}
        />
      )}
      {modal?.kind === 'deny' && (
        <DenyModal
          referrals={modal.referrals}
          notify={notify}
          onClose={() => {
            setModal(null);
            setSelectedIds(new Set());
          }}
        />
      )}
    </div>
  );
}
